import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  ArrowLeft,
  FileSearch,
  BarChart3,
  AlertTriangle,
  Activity,
  Settings2,
  Download,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useReview, useReviewConflicts, useReviewSources } from '@/features/review/hooks/useReview';
import { ScreeningQueue } from '@/features/screening/components/ScreeningQueue';
import { PhaseManager } from '@/features/review/components/PhaseManager';
import { ReviewSettings } from '@/features/review/components/ReviewSettings';
import { ReviewerPanel } from '@/features/review/components/ReviewerPanel';
import { ConflictResolver } from '@/features/review/components/ConflictResolver';
import { IRRMetrics } from '@/features/review/components/IRRMetrics';
import { ExportPanel } from '@/features/review/components/ExportPanel';
import type { ReviewPhase } from '@/features/review/types';

export function Review() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const { data: review, isLoading } = useReview(reviewId);
  const { data: conflicts } = useReviewConflicts(reviewId);
  const { data: reviewSources } = useReviewSources(reviewId);
  const [activeTab, setActiveTab] = useState('screening');

  const unresolvedConflicts = conflicts?.filter((c) => !c.resolved).length ?? 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!review) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <p className="text-sm text-slate-500">Review not found</p>
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => navigate('/team')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Teams
        </Button>
      </div>
    );
  }

  const currentPhase: ReviewPhase =
    review.status === 'full_text' ? 'full_text' : 'screening';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigate('/team')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold text-slate-900">{review.name}</h1>
            <Badge variant="outline" className="text-xs capitalize">
              {review.review_mode.replace('_', ' ')}
            </Badge>
          </div>
          {review.description && (
            <p className="text-sm text-slate-500 ml-10">{review.description}</p>
          )}
        </div>
      </div>

      {/* Phase Manager (always visible) */}
      <PhaseManager review={review} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="screening" className="gap-1.5">
            <FileSearch className="h-3.5 w-3.5" />
            Screening
          </TabsTrigger>
          <TabsTrigger value="overview" className="gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="conflicts" className="gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            Conflicts
            {unresolvedConflicts > 0 && (
              <Badge variant="destructive" className="h-4 min-w-4 px-1 text-[10px] ml-1">
                {unresolvedConflicts}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="metrics" className="gap-1.5">
            <Activity className="h-3.5 w-3.5" />
            Metrics
          </TabsTrigger>
          <TabsTrigger value="export" className="gap-1.5">
            <Download className="h-3.5 w-3.5" />
            Export
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-1.5">
            <Settings2 className="h-3.5 w-3.5" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="screening" className="mt-6">
          {review.status === 'completed' ? (
            <div className="text-center py-12 text-slate-500">
              <p className="text-sm">This review has been completed.</p>
            </div>
          ) : (
            <ScreeningQueue review={review} phase={currentPhase} />
          )}
        </TabsContent>

        <TabsContent value="overview" className="mt-6">
          <ReviewerPanel
            review={review}
            sources={(reviewSources ?? []).map((rs) => ({
              id: rs.source_id,
              title: rs.source?.title ?? null,
              file_name: rs.source?.file_name ?? 'Unknown',
            }))}
            phase={currentPhase}
          />
        </TabsContent>

        <TabsContent value="conflicts" className="mt-6">
          <ConflictResolver
            reviewId={review.id}
            phase={currentPhase}
            canResolve={true}
          />
        </TabsContent>

        <TabsContent value="metrics" className="mt-6">
          <IRRMetrics reviewId={review.id} phase={currentPhase} />
        </TabsContent>

        <TabsContent value="export" className="mt-6">
          <ExportPanel reviewId={review.id} className="max-w-md" />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <ReviewSettings review={review} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
